import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Upload, X, Plus, ChevronLeft, AlertCircle, Copy } from "lucide-react";
import FloLogo from "@/components/FloLogo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { check as _validate, preflightCheck } from "@/lib/kybValidators";

const ENTITY_LABELS = {
  sole_proprietor: "Sole Proprietor",
  partnership: "Partnership",
  llp: "LLP",
  company: "Private Limited / OPC",
};

// Per-entity BO threshold copy — Xflow rules.
const BO_THRESHOLD = { partnership: 15, llp: 15, company: 25, sole_proprietor: null };

// Person roles required per entity type.
const REQUIRED_ROLES = {
  sole_proprietor: [],
  partnership: ["beneficial_owner", "designated_partner", "representative"],
  llp:         ["beneficial_owner", "designated_partner", "representative"],
  company:     ["beneficial_owner", "director", "representative"],
};

const ROLE_LABELS = {
  beneficial_owner: "Beneficial Owner",
  designated_partner: "Designated Partner",
  director: "Business Director",
  representative: "Business Representative",
};

// Xflow-compat: 10 MB, PDF/PNG/JPG/JPEG only.
const MIME_OK = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
const MAX_BYTES = 10 * 1024 * 1024;

function FileField({ label, fileId, onUploaded, onCleared, testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    if (!MIME_OK.includes(f.type)) { toast.error("Only PDF, PNG, JPG"); return; }
    if (f.size > MAX_BYTES) { toast.error("File exceeds 10 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/kyb/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(r.data.file_id, f.name);
      toast.success(`Uploaded: ${f.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-slate-600">{label}</label>
      {fileId ? (
        <div className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
          <span className="flex-1 truncate text-emerald-800">Uploaded ✓ ({fileId.slice(0, 18)}…)</span>
          <button type="button" onClick={onCleared} data-testid={`${testid}-clear`}
            className="text-emerald-700 hover:text-emerald-900"><X size={16}/></button>
        </div>
      ) : (
        <label className="cursor-pointer flex items-center gap-2 text-sm px-3 py-2 rounded-lg border-2 border-dashed border-slate-300 hover:border-slate-400">
          <Upload size={16} className="text-slate-500" />
          <span className="text-slate-600">{busy ? "Uploading…" : "PDF, PNG, JPG · max 10 MB"}</span>
          <input type="file" accept=".pdf,.png,.jpg,.jpeg" hidden
            data-testid={testid}
            disabled={busy}
            onChange={(e) => upload(e.target.files?.[0])} />
        </label>
      )}
    </div>
  );
}

function Input({ label, value, onChange, testid, error, hint, uppercase = false, ...rest }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-slate-600">{label}</label>
      <input
        value={value || ""}
        onChange={(e) => onChange(uppercase ? e.target.value.toUpperCase() : e.target.value)}
        data-testid={testid}
        aria-invalid={!!error}
        className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none ${
          error
            ? "border-red-400 focus:border-red-500 bg-red-50"
            : "border-slate-300 focus:border-[#1E5BD6]"
        }`}
        {...rest}
      />
      {error && (
        <div className="text-xs text-red-600" data-testid={`${testid}-error`}>
          {error}
        </div>
      )}
      {!error && hint && (
        <div className="text-xs text-slate-500">{hint}</div>
      )}
    </div>
  );
}

export default function KybWizard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isDemo = !!user?.is_demo;
  // Wrap the shared validators so demo accounts see no blocking errors —
  // matches the backend bypass for the same accounts. Hints and file uploads
  // still work; only the red inline errors are suppressed.
  const validate = isDemo
    ? new Proxy(_validate, { get: () => () => null })
    : _validate;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("draft");
  const [systemMsg, setSystemMsg] = useState([]);
  const [step, setStep] = useState(0);
  const [kyb, setKyb] = useState({
    entity_type: null,
    generic: { legal_name: "", brand_name: "", dba: "", website: "", no_website: false,
               business_description: "", physical_address: {}, email: user?.email || "" },
    business_ids: {},
    iec: { number: "" },
    persons: [],
    tos_acceptance: null,
  });
  const [tosChecked, setTosChecked] = useState(false);
  const [preflightIssues, setPreflightIssues] = useState([]);

  useEffect(() => {
    api.get("/kyb").then(r => {
      const s = r.data;
      setStatus(s.kyb_status || "draft");
      setSystemMsg(s.system_message || []);
      if (s.kyb && Object.keys(s.kyb).length) {
        setKyb(prev => ({ ...prev, ...s.kyb, generic: { ...prev.generic, ...(s.kyb.generic || {}) } }));
      }
    }).finally(() => setLoading(false));
  }, []);

  const set = (path, val) => setKyb(prev => {
    const c = structuredClone(prev);
    const p = path.split(".");
    let o = c;
    for (let i = 0; i < p.length - 1; i++) { o[p[i]] = o[p[i]] || {}; o = o[p[i]]; }
    o[p[p.length - 1]] = val;
    return c;
  });

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.patch("/kyb/draft", kyb);
      toast.success("Draft saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const submit = async () => {
    if (!tosChecked) { toast.error("Accept the Terms of Service to continue"); return; }
    // Client-side preflight: runs the same rules as services/kyb_service.validate_kyb
    // so we never round-trip a doomed submission through Xflow.
    // Demo accounts skip the preflight — backend accepts anything for them.
    const issues = isDemo ? [] : preflightCheck(kyb);
    if (issues.length) {
      setPreflightIssues(issues);
      toast.error(`${issues.length} item${issues.length > 1 ? "s" : ""} to fix before submitting`);
      return;
    }
    setPreflightIssues([]);
    setSubmitting(true);
    try {
      await api.patch("/kyb/draft", kyb);  // ensure saved first
      const r = await api.post("/kyb/submit", {});
      toast.success("Submitted to Xflow!");
      setStatus(r.data.kyb_status);
      setTimeout(() => nav("/dashboard"), 1500);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.missing_fields) {
        // Server disagreed with us — treat those as preflight issues too.
        setPreflightIssues(d.missing_fields.map((f) => ({ code: f, message: `Server: ${f} is required.` })));
      } else if (d?.errors) {
        toast.error(`${d.stage}: ${d.errors[0]?.message || d.errors[0]?.code}`);
      } else {
        toast.error(e?.response?.data?.detail || "Submit failed");
      }
    } finally { setSubmitting(false); }
  };

  const addPerson = (role) => set("persons", [...(kyb.persons || []), {
    role, first_name: "", last_name: "", pan_number: "", id_document_file_id: null,
  }]);
  const updPerson = (idx, key, val) => {
    const arr = [...kyb.persons];
    arr[idx] = { ...arr[idx], [key]: val };
    set("persons", arr);
  };
  const rmPerson = (idx) => set("persons", kyb.persons.filter((_, i) => i !== idx));

  // Copy identity fields (name + PAN) from the person immediately above in the
  // list. Useful when the same individual holds multiple roles (owner + director
  // + representative), which is common for small proprietorships and LLPs.
  const copyFromAbove = (idx) => {
    if (idx <= 0) return;
    const src = kyb.persons[idx - 1];
    if (!src) return;
    const arr = [...kyb.persons];
    arr[idx] = {
      ...arr[idx],
      first_name: src.first_name || "",
      last_name: src.last_name || "",
      pan_number: src.pan_number || "",
    };
    set("persons", arr);
    toast.success("Copied name & PAN from row above");
  };

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-muted-foreground">
      <FloLogo variant="lockup-light" size={44} />
      <div className="flex items-center text-sm"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading verification…</div>
    </div>
  );

  const et = kyb.entity_type;
  const boThreshold = BO_THRESHOLD[et];
  const readOnly = status === "submitted" || status === "activated";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-3">
        <button onClick={() => nav("/dashboard")} data-testid="kyb-back" className="text-slate-500 hover:text-slate-800">
          <ChevronLeft size={20} />
        </button>
        <FloLogo variant="lockup-light" size={30} />
        <div className="ml-4 text-sm font-semibold text-slate-700">Business Verification (KYB)</div>
        <div className="ml-auto text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-700" data-testid="kyb-status-pill">
          Status: <b className="uppercase">{status.replace(/_/g, " ")}</b>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-6">
        {systemMsg.length > 0 && (
          <div className="mb-6 p-4 rounded-lg bg-amber-50 border border-amber-200" data-testid="kyb-system-message">
            <div className="font-semibold text-amber-900 text-sm mb-1">Xflow needs additional information:</div>
            <ul className="list-disc pl-5 text-sm text-amber-900">
              {systemMsg.map((m, i) => <li key={i}>{typeof m === "string" ? m : (m.message || JSON.stringify(m))}</li>)}
            </ul>
          </div>
        )}

        {/* STEP 0: Entity type + generic */}
        {step === 0 && (
          <section className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6" data-testid="kyb-step-generic">
            <h2 className="text-lg font-bold text-slate-900">Business type & basic details</h2>
            <div>
              <label className="text-xs font-semibold text-slate-600">Entity type</label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {Object.entries(ENTITY_LABELS).map(([k, lbl]) => (
                  <button key={k} type="button"
                    onClick={() => set("entity_type", k)}
                    disabled={readOnly}
                    data-testid={`kyb-entity-${k}`}
                    className={`px-4 py-3 rounded-lg border text-sm text-left ${et === k ? "border-[#1E5BD6] bg-[#EAF2FF] font-semibold" : "border-slate-300 hover:border-slate-400"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            <Input label="Legal name (as registered)" value={kyb.generic.legal_name}
              onChange={v => set("generic.legal_name", v)} testid="kyb-legal-name" disabled={readOnly}/>
            <div className="grid grid-cols-2 gap-4">
              <Input label="DBA (Doing Business As, optional)" value={kyb.generic.dba}
                onChange={v => set("generic.dba", v)} testid="kyb-dba" disabled={readOnly}/>
              <Input label="Brand name (optional)" value={kyb.generic.brand_name}
                onChange={v => set("generic.brand_name", v)} testid="kyb-brand" disabled={readOnly}/>
            </div>
            <div>
              <Input label="Website" value={kyb.generic.website}
                placeholder="https://…"
                onChange={v => set("generic.website", v)} testid="kyb-website" disabled={readOnly || kyb.generic.no_website}/>
              <label className="flex items-center gap-2 text-xs text-slate-600 mt-2">
                <input type="checkbox" checked={!!kyb.generic.no_website}
                  onChange={e => set("generic.no_website", e.target.checked)} data-testid="kyb-no-website"/>
                No website available
              </label>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">Business description (products exported)</label>
              <textarea rows={3} value={kyb.generic.business_description || ""}
                onChange={e => set("generic.business_description", e.target.value)}
                data-testid="kyb-desc" disabled={readOnly}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-[#1E5BD6] focus:outline-none text-sm"/>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input label="Address line 1" value={kyb.generic.physical_address?.line1}
                onChange={v => set("generic.physical_address.line1", v)} testid="kyb-addr-line1"/>
              <Input label="City" value={kyb.generic.physical_address?.city}
                onChange={v => set("generic.physical_address.city", v)} testid="kyb-addr-city"/>
              <Input label="State" value={kyb.generic.physical_address?.state}
                onChange={v => set("generic.physical_address.state", v)} testid="kyb-addr-state"/>
              <Input label="Postal code" value={kyb.generic.physical_address?.postal_code}
                onChange={v => set("generic.physical_address.postal_code", v)} testid="kyb-addr-postal"/>
            </div>
          </section>
        )}

        {/* STEP 1: Business IDs (per type) */}
        {step === 1 && et && (
          <section className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6" data-testid="kyb-step-ids">
            <h2 className="text-lg font-bold text-slate-900">Business identifiers — {ENTITY_LABELS[et]}</h2>

            {et === "sole_proprietor" && (
              <>
                <Input label="PAN number (type or upload below)" value={kyb.business_ids.pan} uppercase
                  onChange={v => set("business_ids.pan", v)} testid="kyb-pan"
                  error={validate.pan(kyb.business_ids.pan)}
                  hint="10 characters: 5 letters + 4 digits + 1 letter"/>
                <FileField label="Or upload PAN card (accepted alternative to typing the number)"
                  fileId={kyb.business_ids.pan_file_id}
                  onUploaded={id => set("business_ids.pan_file_id", id)}
                  onCleared={() => set("business_ids.pan_file_id", null)}
                  testid="kyb-pan-file"/>
                <Input label="GSTIN (type or upload below)"
                  value={kyb.business_ids.gstin} uppercase
                  onChange={v => set("business_ids.gstin", v)} testid="kyb-gstin"
                  error={validate.gstin(kyb.business_ids.gstin)}
                  hint="15 characters: state code + PAN + entity code + Z + checksum. Skip if providing permanent address instead."/>
                <FileField label="Or upload GSTIN certificate (accepted alternative to typing the number)"
                  fileId={kyb.business_ids.gstin_file_id}
                  onUploaded={id => set("business_ids.gstin_file_id", id)}
                  onCleared={() => set("business_ids.gstin_file_id", null)}
                  testid="kyb-gstin-file"/>
                <Input label="Permanent address (fallback if you have no GSTIN)"
                  value={kyb.business_ids.permanent_address}
                  onChange={v => set("business_ids.permanent_address", v)}
                  testid="kyb-perm-address"/>
              </>
            )}
            {et === "partnership" && (
              <>
                <Input label="GSTIN (type or upload below)" value={kyb.business_ids.gstin} uppercase
                  onChange={v => set("business_ids.gstin", v)} testid="kyb-gstin"
                  error={validate.gstin(kyb.business_ids.gstin)}
                  hint="15 characters: state code + PAN + entity code + Z + checksum"/>
                <FileField label="Or upload GSTIN certificate (accepted alternative to typing the number)"
                  fileId={kyb.business_ids.gstin_file_id}
                  onUploaded={id => set("business_ids.gstin_file_id", id)}
                  onCleared={() => set("business_ids.gstin_file_id", null)}
                  testid="kyb-gstin-file"/>
                <FileField label="Partnership deed (upload required — no numeric alternative)"
                  fileId={kyb.business_ids.partnership_deed_file_id}
                  onUploaded={id => set("business_ids.partnership_deed_file_id", id)}
                  onCleared={() => set("business_ids.partnership_deed_file_id", null)}
                  testid="kyb-deed"/>
              </>
            )}
            {(et === "llp" || et === "company") && (
              <>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">Primary business ID (choose one)</label>
                  <select value={kyb.business_ids.primary_id_type || ""}
                    onChange={e => set("business_ids.primary_id_type", e.target.value)}
                    data-testid="kyb-primary-id-type"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm">
                    <option value="">Select…</option>
                    {et === "llp" && <option value="llpin">LLPIN (Incorporation number)</option>}
                    {et === "company" && <option value="cin">CIN (Incorporation number)</option>}
                    <option value="gstin">GSTIN</option>
                    <option value="pan">Business PAN</option>
                  </select>
                </div>
                <Input label="Number (type or upload certificate below)" value={kyb.business_ids.primary_id_number} uppercase
                  onChange={v => set("business_ids.primary_id_number", v)} testid="kyb-primary-id-number"
                  error={
                    kyb.business_ids.primary_id_type && kyb.business_ids.primary_id_number
                      ? validate[kyb.business_ids.primary_id_type]?.(kyb.business_ids.primary_id_number)
                      : null
                  }
                  hint={
                    kyb.business_ids.primary_id_type === "cin"   ? "21 characters: L/U + 5-digit industry code + 2-letter state + 4-digit year + 3-letter category + 6-digit registration" :
                    kyb.business_ids.primary_id_type === "gstin" ? "15 characters: state code + PAN + entity code + Z + checksum" :
                    kyb.business_ids.primary_id_type === "pan"   ? "10 characters: 5 letters + 4 digits + 1 letter" :
                    kyb.business_ids.primary_id_type === "llpin" ? "7 characters: 3 letters + hyphen + 4 digits" : null
                  }/>
                <FileField label="Or upload the corresponding certificate (accepted alternative to typing the number)"
                  fileId={kyb.business_ids.primary_id_file_id}
                  onUploaded={id => set("business_ids.primary_id_file_id", id)}
                  onCleared={() => set("business_ids.primary_id_file_id", null)}
                  testid="kyb-primary-id-file"/>
              </>
            )}

            <Input label="IEC (Import-Export Code)" value={kyb.iec?.number} uppercase
              onChange={v => set("iec.number", v)} testid="kyb-iec"
              error={validate.pan(kyb.iec?.number)}
              hint="Same 10-character format as PAN (issued by DGFT)"/>
          </section>
        )}

        {/* STEP 2: Persons */}
        {step === 2 && et && (
          <section className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6" data-testid="kyb-step-persons">
            <h2 className="text-lg font-bold text-slate-900">
              People — {ENTITY_LABELS[et]}
              {boThreshold && <span className="text-sm font-normal text-slate-500 ml-2">
                (BO threshold: {boThreshold}%)
              </span>}
            </h2>

            {(REQUIRED_ROLES[et] || []).map(role => {
              const rolePersons = (kyb.persons || []).filter(p => p.role === role);
              return (
                <div key={role} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-sm text-slate-800">{ROLE_LABELS[role]}</div>
                    <button type="button" onClick={() => addPerson(role)}
                      data-testid={`kyb-add-${role}`}
                      className="text-xs font-semibold text-[#1E5BD6] hover:opacity-80 flex items-center gap-1">
                      <Plus size={14}/>Add
                    </button>
                  </div>
                  {rolePersons.length === 0 && <div className="text-xs text-slate-400 italic">None added yet</div>}
                  {(kyb.persons || []).map((p, idx) => p.role === role && (
                    <div key={idx} className="p-4 rounded-lg border border-slate-200 space-y-3 bg-slate-50">
                      <div className="flex items-center justify-between">
                        {idx > 0 ? (
                          <button
                            type="button"
                            onClick={() => copyFromAbove(idx)}
                            data-testid={`kyb-person-${idx}-copy-above`}
                            className="text-xs font-semibold text-[#1E5BD6] hover:opacity-80 flex items-center gap-1">
                            <Copy size={12}/>Copy name & PAN from row above
                          </button>
                        ) : <span />}
                        <button onClick={() => rmPerson(idx)} className="text-slate-400 hover:text-red-600" data-testid={`kyb-person-${idx}-rm`}>
                          <X size={16}/>
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Input label="First name" value={p.first_name}
                          onChange={v => updPerson(idx, "first_name", v)} testid={`kyb-person-${idx}-fn`}/>
                        <Input label="Last name" value={p.last_name}
                          onChange={v => updPerson(idx, "last_name", v)} testid={`kyb-person-${idx}-ln`}/>
                      </div>
                      <Input label="PAN number (as on card — or upload PAN/Passport below)" value={p.pan_number} uppercase
                        onChange={v => updPerson(idx, "pan_number", v)} testid={`kyb-person-${idx}-pan`}
                        error={validate.pan(p.pan_number)}
                        hint="10 characters: 5 letters + 4 digits + 1 letter"/>
                      <FileField label="Upload PAN card or Passport (upload-only if using Passport)"
                        fileId={p.id_document_file_id}
                        onUploaded={id => updPerson(idx, "id_document_file_id", id)}
                        onCleared={() => updPerson(idx, "id_document_file_id", null)}
                        testid={`kyb-person-${idx}-doc`}/>
                      {role === "beneficial_owner" && (
                        <Input label={`Ownership % (>= ${boThreshold})`}
                          value={p.percent_ownership || ""} type="number"
                          onChange={v => updPerson(idx, "percent_ownership", v)}
                          testid={`kyb-person-${idx}-pct`}/>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}

            {et === "sole_proprietor" && (
              <div className="text-sm text-slate-600 italic">
                As a sole proprietor, no additional persons are required. Click Next to review and submit.
              </div>
            )}
          </section>
        )}

        {/* STEP 3: Review & TOS */}
        {step === 3 && et && (
          <section className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4" data-testid="kyb-step-review">
            <h2 className="text-lg font-bold text-slate-900">Review & submit</h2>
            <div className="p-4 rounded-lg bg-slate-50 text-sm space-y-2">
              <div><b>Entity:</b> {ENTITY_LABELS[et]}</div>
              <div><b>Legal name:</b> {kyb.generic.legal_name || "—"}</div>
              <div><b>Persons:</b> {(kyb.persons || []).length}</div>
              <div><b>IEC:</b> {kyb.iec?.number || "—"}</div>
            </div>

            {preflightIssues.length > 0 && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-200" data-testid="kyb-preflight-issues">
                <div className="flex items-center gap-2 font-semibold text-red-800 text-sm mb-2">
                  <AlertCircle className="h-4 w-4" />
                  Fix {preflightIssues.length} item{preflightIssues.length > 1 ? "s" : ""} before submitting
                </div>
                <ul className="list-disc pl-6 text-sm text-red-800 space-y-1">
                  {preflightIssues.map((iss, i) => (
                    <li key={`${iss.code}-${i}`} data-testid={`kyb-issue-${iss.code}`}>{iss.message}</li>
                  ))}
                </ul>
              </div>
            )}

            <label className="flex items-start gap-2 text-sm text-slate-700 pt-2">
              <input type="checkbox" checked={tosChecked} onChange={e => setTosChecked(e.target.checked)}
                data-testid="kyb-tos-checkbox" className="mt-1"/>
              <span>
                I accept Flo Payments and Xflow's <a href="https://useflopayments.com/terms" className="text-[#1E5BD6] underline" target="_blank" rel="noreferrer">Terms of Service</a> and confirm that the information provided is accurate.
              </span>
            </label>
          </section>
        )}

        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={step === 0}
            data-testid="kyb-prev-btn"
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40">
            ← Back
          </button>
          <div className="flex gap-3">
            <button
              onClick={saveDraft}
              disabled={saving || readOnly}
              data-testid="kyb-save-draft-btn"
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50">
              {saving ? "Saving…" : "Save draft"}
            </button>
            {step < 3 ? (
              <button
                onClick={async () => {
                  await api.patch("/kyb/draft", kyb).catch(() => {});
                  setStep(s => s + 1);
                }}
                disabled={!et}
                data-testid="kyb-next-btn"
                className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#1E5BD6] text-white hover:opacity-90 disabled:opacity-40">
                Next
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting || !tosChecked || readOnly}
                data-testid="kyb-submit-btn"
                className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#1E5BD6] text-white hover:opacity-90 disabled:opacity-40 flex items-center gap-2">
                {submitting && <Loader2 className="h-4 w-4 animate-spin"/>}
                Submit for verification
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
